import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { apiService } from '@/services/api';
import { Button } from '@/components/shared';
import { HomeIcon, HeartIcon, HandshakeIcon, UserGroupIcon, BriefcaseIcon, MailIcon, StarIcon, CheckCircleIcon } from '../shared/icons';
import { useAnalytics } from '@/hooks/useAnalytics';

// Types for the dynamic form views
type ViewType = 'initial' | 'in-area' | 'out-of-area' | 'contact' | 'success';
type InterestType = 'GETTING_HELP' | 'VOLUNTEERING' | 'PROFESSIONAL_SERVICES';

// API response types
interface ServiceAreaValidation {
    isServiceable: boolean;
    cityName: string;
    stateName: string;
}

interface InterestSubmissionResponse {
    success: boolean;
    interestLogId: string;
    message: string;
}

interface ContactSubmissionResponse {
    success: boolean;
    message: string;
}

// Form schemas
const initialFormSchema = z.object({
    email: z.string().email('Please enter a valid email address'),
    zipCode: z.string().regex(/^\d{5}$/, 'Please enter a valid 5-digit ZIP code')
});

const interestFormSchema = z.object({
    email: z.string().email('Please enter a valid email address'),
    zipCode: z.string().regex(/^\d{5}$/, 'Please enter a valid 5-digit ZIP code'),
    firstName: z.string().min(1, 'Please enter your first name'),
    lastName: z.string().min(1, 'Please enter your last name'),
    interestType: z.enum(['GETTING_HELP', 'VOLUNTEERING', 'PROFESSIONAL_SERVICES'], {
        errorMap: () => ({ message: 'Please select your area of interest' })
    })
});

const contactFormSchema = z.object({
    fullName: z.string().min(1, 'Please enter your full name'),
    email: z.string().email('Please enter a valid email address'),
    subject: z.string().min(1, 'Please enter a subject'),
    message: z.string().min(10, 'Please enter a message of at least 10 characters')
});

interface GetInvolvedHubProps {
    className?: string;
    preselectedInterest?: InterestType;
}

export const GetInvolvedHub: React.FC<GetInvolvedHubProps> = ({
    className = '',
    preselectedInterest
}) => {
    // Analytics tracking
    const { trackUserAction } = useAnalytics();

    // Component state
    const [currentView, setCurrentView] = useState<ViewType>('initial');
    const [isLoading, setIsLoading] = useState(false);
    const [zipValidation, setZipValidation] = useState<ServiceAreaValidation | null>(null);
    const [submitMessage, setSubmitMessage] = useState<string>('');
    const [submitTitle, setSubmitTitle] = useState<string>('');
    const [submitIconType, setSubmitIconType] = useState<string>('check');

    // Track which fields have been touched (blurred) to control error display
    const [touchedFields, setTouchedFields] = useState<{
        initialEmail?: boolean;
        initialZip?: boolean;
        interestEmail?: boolean;
        interestZip?: boolean;
        interestFirstName?: boolean;
        interestLastName?: boolean;
        contactFullName?: boolean;
        contactEmail?: boolean;
        contactSubject?: boolean;
        contactMessage?: boolean;
    }>({});

    // Form instances
    const initialForm = useForm({
        resolver: zodResolver(initialFormSchema),
        defaultValues: { email: '', zipCode: '' },
        mode: 'onChange', // Validate in real-time as user types
        reValidateMode: 'onChange' // Re-validate in real-time after first validation
    });

    const interestForm = useForm({
        resolver: zodResolver(interestFormSchema),
        defaultValues: {
            email: '',
            zipCode: '',
            firstName: '',
            lastName: '',
            interestType: preselectedInterest || undefined
        },
        mode: 'onChange', // Validate in real-time as user types
        reValidateMode: 'onChange' // Re-validate in real-time after first validation
    });

    const contactForm = useForm({
        resolver: zodResolver(contactFormSchema),
        defaultValues: { fullName: '', email: '', subject: '', message: '' },
        mode: 'onChange', // Validate in real-time as user types
        reValidateMode: 'onChange' // Re-validate in real-time after first validation
    });

    // ZIP code validation with debouncing
    useEffect(() => {
        const zipCode = initialForm.watch('zipCode');
        if (zipCode && zipCode.length === 5) {
            const timeoutId = setTimeout(async () => {
                try {
                    setIsLoading(true);
                    const response = await apiService.get<ServiceAreaValidation>(
                        `/service-areas/validation?zip=${zipCode}`
                    );
                    setZipValidation(response);
                } catch (error) {
                    console.error('ZIP validation error:', error);
                    // On error, assume not serviceable with generic info
                    setZipValidation({
                        isServiceable: false,
                        cityName: 'your area',
                        stateName: ''
                    });
                } finally {
                    setIsLoading(false);
                }
            }, 500);

            return () => clearTimeout(timeoutId);
        }
    }, [initialForm.watch('zipCode')]);

    // Handle initial form submission
    const handleInitialSubmit = async (data: { email: string; zipCode: string }) => {
        if (!zipValidation) return;

        // Track initial form submission
        trackUserAction.formSubmitAttempt('initial_form', {
            has_zip_validation: !!zipValidation,
            is_serviceable_area: zipValidation.isServiceable
        });

        // Transfer data to interest form
        interestForm.setValue('email', data.email);
        interestForm.setValue('zipCode', data.zipCode);

        // Set appropriate view based on service area
        setCurrentView(zipValidation.isServiceable ? 'in-area' : 'out-of-area');

        // Track path progression
        trackUserAction.journeyMilestone('moved_to_interest_form', {
            service_area_status: zipValidation.isServiceable ? 'in_area' : 'out_of_area',
            zip_code: data.zipCode
        });
    };

    // Handle interest form submission
    const handleInterestSubmit = async (data: { email: string; zipCode: string; firstName: string; lastName: string; interestType?: InterestType }) => {
        if (!data.interestType) {
            console.error('Interest type is required');
            trackUserAction.formValidationError('interestType', 'required', 'interest_form');
            return;
        }

        try {
            setIsLoading(true);

            // Track form submission attempt
            trackUserAction.formSubmitAttempt('interest_form', {
                interest_type: data.interestType,
                zip_code: data.zipCode,
                is_serviceable_area: zipValidation?.isServiceable || false
            });

            // Map new interest types to legacy values expected by /early-interest endpoint
            const legacyInterestTypeMap = {
                'GETTING_HELP': 'NEED_HELP',
                'VOLUNTEERING': 'WANT_TO_VOLUNTEER',
                'PROFESSIONAL_SERVICES': 'CARE_PROFESSIONAL'
            } as const;

            await apiService.post<InterestSubmissionResponse>('/early-interest', {
                email: data.email,
                zipCode: data.zipCode,
                firstName: data.firstName,
                lastName: data.lastName,
                interestType: legacyInterestTypeMap[data.interestType as keyof typeof legacyInterestTypeMap]
            });

            // Track successful submission
            trackUserAction.formSubmitSuccess('interest_form', {
                interest_type: data.interestType,
                zip_code: data.zipCode,
                is_serviceable_area: zipValidation?.isServiceable || false
            });

            // Track business milestone
            trackUserAction.businessEvent('early_interest_signup', {
                user_type: data.interestType,
                zip_code: data.zipCode,
                service_area: zipValidation?.isServiceable ? 'serviceable' : 'not_serviceable'
            });

            // Custom success message based on service area
            const confirmationContent = getConfirmationContent(
                data.interestType,
                data.zipCode,
                zipValidation?.isServiceable || false
            );

            setSubmitTitle(confirmationContent.title);
            setSubmitMessage(confirmationContent.message);
            setSubmitIconType(confirmationContent.icon);
            setCurrentView('success');
        } catch (error) {
            console.error('Interest submission error:', error);

            // Track form submission error
            trackUserAction.formSubmitError('interest_form', error instanceof Error ? error.message : 'Unknown error');

            alert('There was an error submitting your information. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    // Handle contact form submission
    const handleContactSubmit = async (data: { fullName: string; email: string; subject: string; message: string }) => {
        try {
            setIsLoading(true);

            // Track contact form submission attempt
            trackUserAction.formSubmitAttempt('contact_form', {
                subject: data.subject,
                message_length: data.message.length
            });

            await apiService.post<ContactSubmissionResponse>('/contact', data);

            // Track successful contact submission
            trackUserAction.formSubmitSuccess('contact_form', {
                subject: data.subject,
                message_length: data.message.length
            });

            // Track business event
            trackUserAction.businessEvent('contact_form_submission', {
                subject: data.subject,
                message_length: data.message.length
            });

            const contactContent = getContactConfirmationContent();
            setSubmitTitle(contactContent.title);
            setSubmitMessage(contactContent.message);
            setSubmitIconType(contactContent.icon);
            setCurrentView('success');
        } catch (error) {
            console.error('Contact submission error:', error);

            // Track contact form error
            trackUserAction.formSubmitError('contact_form', error instanceof Error ? error.message : 'Unknown error');

            alert('There was an error sending your message. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    // Switch to contact view
    const showContactForm = () => {
        // Track contact form opening
        trackUserAction.contactFormOpen('get_involved_hub');

        // Pre-fill email if available
        const currentEmail = initialForm.getValues('email') || interestForm.getValues('email');
        if (currentEmail) {
            contactForm.setValue('email', currentEmail);
        }
        setCurrentView('contact');
    };

    // Return to previous view
    const goBack = () => {
        if (currentView === 'contact') {
            if (zipValidation) {
                setCurrentView(zipValidation.isServiceable ? 'in-area' : 'out-of-area');
            } else {
                setCurrentView('initial');
            }
        } else if (currentView === 'in-area' || currentView === 'out-of-area') {
            setCurrentView('initial');
        }
    };

    // Interest type options
    const interestOptions = [
        { value: 'GETTING_HELP', label: 'Getting help', description: 'I need assistance with daily tasks' },
        { value: 'VOLUNTEERING', label: 'Volunteering', description: 'I want to help others in my community' },
        { value: 'PROFESSIONAL_SERVICES', label: 'Providing professional services', description: 'I offer skilled services to support seniors' }
    ];

    // Function to get confirmation content based on scenario
    const getConfirmationContent = (interestType: InterestType, zipCode: string, isServiceable: boolean) => {
        const scenarios = {
            'GETTING_HELP': {
                serviceable: {
                    icon: 'home',
                    title: 'Welcome to the BiBi community!',
                    message: `Thank you for your interest in staying independent at home with community support. You're part of our Wichita launch area!

What happens next:
• Check your email for detailed founding member information
• You'll be among the first notified when we launch in Wichita
• We'll keep you updated on volunteer recruitment and partnerships in your area
• No spam - just meaningful updates about bringing BiBi to your neighborhood

Your early interest helps us build the support network you deserve.`
                },
                nonServiceable: {
                    icon: 'star',
                    title: 'Thank you for your interest in BiBi!',
                    message: `We'd love to help seniors in the ${zipCode} area age in place with dignity and community support.

What happens next:
• Check your email for information about expansion planning
• You're now on our priority list for the ${zipCode} area
• We'll update you on progress toward serving your neighborhood
• Your interest helps us decide where to expand next

The more neighbors who express interest, the sooner we can bring BiBi to ${zipCode}!`
                }
            },
            'VOLUNTEERING': {
                serviceable: {
                    icon: 'heart',
                    title: 'Welcome to the BiBi volunteer family!',
                    message: `Thank you for wanting to help Wichita seniors stay independent at home. Your generous spirit will make a real difference!

What happens next:
• Check your email for volunteer program details
• You'll be among the first invited to volunteer orientation
• We'll update you on training, background checks, and community events
• Connect with other volunteers who share your passion for helping

Your willingness to help is building a stronger Wichita community.`
                },
                nonServiceable: {
                    icon: 'handshake',
                    title: 'Thank you for wanting to volunteer with BiBi!',
                    message: `Your generous spirit could help bring community support to seniors in the ${zipCode} area.

What happens next:
• Check your email for expansion updates and volunteer information
• You're now on our volunteer interest list for the ${zipCode} area
• We'll update you on expansion planning and volunteer opportunities
• Strong volunteer interest helps us prioritize new areas

Help us grow the movement by sharing BiBi with friends and neighbors!`
                }
            },
            'PROFESSIONAL_SERVICES': {
                serviceable: {
                    icon: 'usergroup',
                    title: 'Welcome to BiBi\'s professional network!',
                    message: `Thank you for your interest in providing mission-driven care to Wichita seniors through our platform.

What happens next:
• Check your email for professional partner program details
• You'll be among the first invited to join our credentialing process
• We'll update you on platform fees, training, and networking opportunities
• Learn about our referral system and volunteer-first model

Your expertise will help seniors receive the professional care they need.`
                },
                nonServiceable: {
                    icon: 'briefcase',
                    title: 'Thank you for your professional interest in BiBi!',
                    message: `Your expertise could help us bring quality senior services to the ${zipCode} area.

What happens next:
• Check your email for expansion updates and professional program information
• You're now on our professional interest list for the ${zipCode} area
• We'll update you on expansion planning and credentialing requirements
• Your input helps us understand local market needs

Professional interest like yours helps us plan successful expansion.`
                }
            }
        };

        const scenario = scenarios[interestType];
        return isServiceable ? scenario.serviceable : scenario.nonServiceable;
    };

    // Function to get contact form confirmation content
    const getContactConfirmationContent = () => ({
        icon: 'mail',
        title: 'Message sent successfully!',
        message: `Thanks for reaching out to the BiBi team. We'll get back to you soon!

What happens next:
• Our team will review your message within one business day
• You'll receive a personal response based on your inquiry
• Check your email for a confirmation of your message

We appreciate you taking the time to contact us.`
    });

    return (
        <section className={`py-16 bg-gradient-to-br from-purple-50 to-blue-50 get-involved-hub ${className}`}>
            <div className="container mx-auto px-4">
                {/* Header */}
                <div className="text-center mb-12">
                    <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                        Get Early Access to BiBi
                    </h2>
                    <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                        Be among the first to get help, volunteer, or offer your professional services.
                        As a founding member, you'll get exclusive benefits and your feedback will directly shape our platform.
                        Not in Wichita? Your interest tells us where to launch next!
                    </p>
                </div>

                <div className="max-w-2xl mx-auto">
                    <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">

                        {/* Initial View */}
                        {currentView === 'initial' && (
                            <div className="p-8">
                                <form onSubmit={initialForm.handleSubmit(handleInitialSubmit)} className="space-y-6">
                                    <div>
                                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                                            Email Address
                                        </label>
                                        <input
                                            id="email"
                                            type="email"
                                            {...initialForm.register('email')}
                                            onBlur={() => setTouchedFields(prev => ({ ...prev, initialEmail: true }))}
                                            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors ${initialForm.formState.errors.email && touchedFields.initialEmail
                                                ? 'border-red-300 bg-red-50'
                                                : 'border-gray-300'
                                                }`}
                                            placeholder="your.email@example.com"
                                        />
                                        {initialForm.formState.errors.email && touchedFields.initialEmail && (
                                            <p className="mt-1 text-sm text-red-600">{initialForm.formState.errors.email.message}</p>
                                        )}
                                    </div>

                                    <div>
                                        <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700 mb-2">
                                            ZIP Code
                                        </label>
                                        <input
                                            id="zipCode"
                                            type="text"
                                            inputMode="numeric"
                                            maxLength={5}
                                            {...initialForm.register('zipCode')}
                                            onBlur={() => setTouchedFields(prev => ({ ...prev, initialZip: true }))}
                                            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors ${initialForm.formState.errors.zipCode && touchedFields.initialZip
                                                ? 'border-red-300 bg-red-50'
                                                : 'border-gray-300'
                                                }`}
                                            placeholder="12345"
                                        />
                                        {initialForm.formState.errors.zipCode && touchedFields.initialZip && (
                                            <p className="mt-1 text-sm text-red-600">{initialForm.formState.errors.zipCode.message}</p>
                                        )}
                                    </div>

                                    <Button
                                        type="submit"
                                        variant="gradient"
                                        size="lg"
                                        fullWidth
                                        disabled={!initialForm.formState.isValid || !zipValidation || isLoading}
                                        isLoading={isLoading}
                                    >
                                        {isLoading ? 'Checking...' : 'Continue'}
                                    </Button>
                                </form>

                                {/* Contact link */}
                                <div className="mt-6 text-center">
                                    <Button
                                        variant="ghost"
                                        onClick={showContactForm}
                                        className="text-purple-600 hover:text-purple-700 font-medium underline"
                                    >
                                        Have questions or feedback? Get in touch
                                    </Button>
                                </div>
                            </div>
                        )}

                        {/* In-Area View */}
                        {currentView === 'in-area' && zipValidation && (
                            <div className="p-8">
                                {/* Back button */}
                                <Button
                                    variant="ghost"
                                    onClick={goBack}
                                    className="mb-6 flex items-center text-purple-600 hover:text-purple-700 font-medium !p-0 !min-h-0"
                                    leftIcon={
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                        </svg>
                                    }
                                >
                                    Back
                                </Button>

                                {/* Success message */}
                                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                                    <div className="flex items-center">
                                        <svg className="w-6 h-6 text-green-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        <p className="text-green-800 font-medium">
                                            Great news! BiBi is available in {zipValidation.cityName}, {zipValidation.stateName}.
                                        </p>
                                    </div>
                                </div>

                                {/* Founding member benefits */}
                                <div className="mb-6 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                                    <h4 className="font-semibold text-purple-900 mb-2">Founding Member Benefits</h4>
                                    <ul className="text-sm text-purple-800 space-y-1">
                                        <li>• First to know when we're ready to launch</li>
                                        <li>• Help shape BiBi's services through feedback</li>
                                        <li>• Connect with our team before public launch</li>
                                    </ul>
                                </div>

                                <form onSubmit={interestForm.handleSubmit(handleInterestSubmit)} className="space-y-6">
                                    {/* Name Fields */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                                                First Name
                                            </label>
                                            <input
                                                id="firstName"
                                                type="text"
                                                {...interestForm.register('firstName')}
                                                onBlur={() => setTouchedFields(prev => ({ ...prev, interestFirstName: true }))}
                                                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors ${interestForm.formState.errors.firstName && touchedFields.interestFirstName
                                                    ? 'border-red-300 bg-red-50'
                                                    : 'border-gray-300'
                                                    }`}
                                                placeholder="Your first name"
                                            />
                                            {interestForm.formState.errors.firstName && touchedFields.interestFirstName && (
                                                <p className="mt-1 text-sm text-red-600">{interestForm.formState.errors.firstName.message}</p>
                                            )}
                                        </div>
                                        <div>
                                            <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                                                Last Name
                                            </label>
                                            <input
                                                id="lastName"
                                                type="text"
                                                {...interestForm.register('lastName')}
                                                onBlur={() => setTouchedFields(prev => ({ ...prev, interestLastName: true }))}
                                                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors ${interestForm.formState.errors.lastName && touchedFields.interestLastName
                                                    ? 'border-red-300 bg-red-50'
                                                    : 'border-gray-300'
                                                    }`}
                                                placeholder="Your last name"
                                            />
                                            {interestForm.formState.errors.lastName && touchedFields.interestLastName && (
                                                <p className="mt-1 text-sm text-red-600">{interestForm.formState.errors.lastName.message}</p>
                                            )}
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-3">
                                            I am interested in:
                                        </label>
                                        <div className="space-y-3">
                                            {interestOptions.map((option) => (
                                                <label key={option.value} className="flex items-start space-x-3 cursor-pointer">
                                                    <input
                                                        type="radio"
                                                        value={option.value}
                                                        {...interestForm.register('interestType', {
                                                            onChange: (e) => {
                                                                // Track interest type selection
                                                                trackUserAction.interestTypeSelect(e.target.value);
                                                            }
                                                        })}
                                                        className="mt-1 h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300"
                                                    />
                                                    <div>
                                                        <div className="font-medium text-gray-900">{option.label}</div>
                                                        <div className="text-sm text-gray-600">{option.description}</div>
                                                    </div>
                                                </label>
                                            ))}
                                        </div>
                                        {interestForm.formState.errors.interestType && (
                                            <p className="mt-2 text-sm text-red-600">{interestForm.formState.errors.interestType.message}</p>
                                        )}
                                    </div>

                                    <Button
                                        type="submit"
                                        variant="gradient"
                                        size="lg"
                                        fullWidth
                                        disabled={!interestForm.formState.isValid || isLoading}
                                        isLoading={isLoading}
                                    >
                                        {isLoading ? 'Joining...' : 'Join Early Access List'}
                                    </Button>
                                </form>

                                {/* Contact link */}
                                <div className="mt-6 text-center">
                                    <button
                                        onClick={showContactForm}
                                        className="text-purple-600 hover:text-purple-700 font-medium underline"
                                    >
                                        Have questions or feedback? Get in touch
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Out-of-Area View */}
                        {currentView === 'out-of-area' && zipValidation && (
                            <div className="p-8">
                                {/* Back button */}
                                <Button
                                    variant="ghost"
                                    onClick={goBack}
                                    className="mb-6 flex items-center text-purple-600 hover:text-purple-700 font-medium !p-0 !min-h-0"
                                    leftIcon={
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                        </svg>
                                    }
                                >
                                    Back
                                </Button>

                                {/* Info message */}
                                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                    <p className="text-blue-800 font-medium mb-2">
                                        BiBi isn't available in {interestForm.getValues('zipCode')} yet. We're a brand-new platform launching first in the Wichita metro area, but we're planning our next markets based on community interest.
                                    </p>
                                </div>

                                <form onSubmit={interestForm.handleSubmit(handleInterestSubmit)} className="space-y-6">
                                    {/* Name Fields */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label htmlFor="firstName-out" className="block text-sm font-medium text-gray-700 mb-2">
                                                First Name
                                            </label>
                                            <input
                                                id="firstName-out"
                                                type="text"
                                                {...interestForm.register('firstName')}
                                                onBlur={() => setTouchedFields(prev => ({ ...prev, interestFirstName: true }))}
                                                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors ${interestForm.formState.errors.firstName && touchedFields.interestFirstName
                                                    ? 'border-red-300 bg-red-50'
                                                    : 'border-gray-300'
                                                    }`}
                                                placeholder="Your first name"
                                            />
                                            {interestForm.formState.errors.firstName && touchedFields.interestFirstName && (
                                                <p className="mt-1 text-sm text-red-600">{interestForm.formState.errors.firstName.message}</p>
                                            )}
                                        </div>
                                        <div>
                                            <label htmlFor="lastName-out" className="block text-sm font-medium text-gray-700 mb-2">
                                                Last Name
                                            </label>
                                            <input
                                                id="lastName-out"
                                                type="text"
                                                {...interestForm.register('lastName')}
                                                onBlur={() => setTouchedFields(prev => ({ ...prev, interestLastName: true }))}
                                                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors ${interestForm.formState.errors.lastName && touchedFields.interestLastName
                                                    ? 'border-red-300 bg-red-50'
                                                    : 'border-gray-300'
                                                    }`}
                                                placeholder="Your last name"
                                            />
                                            {interestForm.formState.errors.lastName && touchedFields.interestLastName && (
                                                <p className="mt-1 text-sm text-red-600">{interestForm.formState.errors.lastName.message}</p>
                                            )}
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-3">
                                            I'm interested in:
                                        </label>
                                        <div className="space-y-3">
                                            {interestOptions.map((option) => (
                                                <label key={option.value} className="flex items-start space-x-3 cursor-pointer">
                                                    <input
                                                        type="radio"
                                                        value={option.value}
                                                        {...interestForm.register('interestType', {
                                                            onChange: (e) => {
                                                                // Track interest type selection
                                                                trackUserAction.interestTypeSelect(e.target.value);
                                                            }
                                                        })}
                                                        className="mt-1 h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300"
                                                    />
                                                    <div>
                                                        <div className="font-medium text-gray-900">{option.label}</div>
                                                        <div className="text-sm text-gray-600">{option.description}</div>
                                                    </div>
                                                </label>
                                            ))}
                                        </div>
                                        {interestForm.formState.errors.interestType && (
                                            <p className="mt-2 text-sm text-red-600">{interestForm.formState.errors.interestType.message}</p>
                                        )}
                                    </div>

                                    {/* Privacy note */}
                                    <div className="text-sm text-gray-600 text-center">
                                        We'll only email you with meaningful updates about BiBi's launch in your area - no spam, ever.
                                    </div>

                                    <Button
                                        type="submit"
                                        variant="gradient"
                                        size="lg"
                                        fullWidth
                                        disabled={!interestForm.formState.isValid || isLoading}
                                        isLoading={isLoading}
                                    >
                                        {isLoading ? 'Submitting...' : 'Help Us Plan Our Expansion'}
                                    </Button>
                                </form>

                                {/* Contact link */}
                                <div className="mt-6 text-center">
                                    <Button
                                        variant="ghost"
                                        onClick={showContactForm}
                                        className="text-purple-600 hover:text-purple-700 font-medium underline"
                                    >
                                        Have questions or feedback? Get in touch
                                    </Button>
                                </div>
                            </div>
                        )}

                        {/* Contact Form View */}
                        {currentView === 'contact' && (
                            <div className="p-8">
                                {/* Back button */}
                                <Button
                                    variant="ghost"
                                    onClick={goBack}
                                    className="mb-6 flex items-center text-purple-600 hover:text-purple-700 font-medium !p-0 !min-h-0"
                                    leftIcon={
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                        </svg>
                                    }
                                >
                                    Back
                                </Button>

                                <h3 className="text-xl font-semibold text-gray-900 mb-6">Get in Touch</h3>

                                <form onSubmit={contactForm.handleSubmit(handleContactSubmit)} className="space-y-6">
                                    <div>
                                        <label htmlFor="contact-fullName" className="block text-sm font-medium text-gray-700 mb-2">
                                            Full Name
                                        </label>
                                        <input
                                            id="contact-fullName"
                                            type="text"
                                            {...contactForm.register('fullName')}
                                            onBlur={() => setTouchedFields(prev => ({ ...prev, contactFullName: true }))}
                                            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors ${contactForm.formState.errors.fullName && touchedFields.contactFullName
                                                ? 'border-red-300 bg-red-50'
                                                : 'border-gray-300'
                                                }`}
                                            placeholder="John Doe"
                                        />
                                        {contactForm.formState.errors.fullName && touchedFields.contactFullName && (
                                            <p className="mt-1 text-sm text-red-600">{contactForm.formState.errors.fullName.message}</p>
                                        )}
                                    </div>

                                    <div>
                                        <label htmlFor="contact-email" className="block text-sm font-medium text-gray-700 mb-2">
                                            Email Address
                                        </label>
                                        <input
                                            id="contact-email"
                                            type="email"
                                            {...contactForm.register('email')}
                                            onBlur={() => setTouchedFields(prev => ({ ...prev, contactEmail: true }))}
                                            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors ${contactForm.formState.errors.email && touchedFields.contactEmail
                                                ? 'border-red-300 bg-red-50'
                                                : 'border-gray-300'
                                                }`}
                                            placeholder="your.email@example.com"
                                        />
                                        {contactForm.formState.errors.email && touchedFields.contactEmail && (
                                            <p className="mt-1 text-sm text-red-600">{contactForm.formState.errors.email.message}</p>
                                        )}
                                    </div>

                                    <div>
                                        <label htmlFor="contact-subject" className="block text-sm font-medium text-gray-700 mb-2">
                                            Subject
                                        </label>
                                        <input
                                            id="contact-subject"
                                            type="text"
                                            {...contactForm.register('subject')}
                                            onBlur={() => setTouchedFields(prev => ({ ...prev, contactSubject: true }))}
                                            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors ${contactForm.formState.errors.subject && touchedFields.contactSubject
                                                ? 'border-red-300 bg-red-50'
                                                : 'border-gray-300'
                                                }`}
                                            placeholder="What would you like to discuss?"
                                        />
                                        {contactForm.formState.errors.subject && touchedFields.contactSubject && (
                                            <p className="mt-1 text-sm text-red-600">{contactForm.formState.errors.subject.message}</p>
                                        )}
                                    </div>

                                    <div>
                                        <label htmlFor="contact-message" className="block text-sm font-medium text-gray-700 mb-2">
                                            Message
                                        </label>
                                        <textarea
                                            id="contact-message"
                                            rows={4}
                                            {...contactForm.register('message')}
                                            onBlur={() => setTouchedFields(prev => ({ ...prev, contactMessage: true }))}
                                            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors resize-none ${contactForm.formState.errors.message && touchedFields.contactMessage
                                                ? 'border-red-300 bg-red-50'
                                                : 'border-gray-300'
                                                }`}
                                            placeholder="Tell us more about your question or interest..."
                                        />
                                        {contactForm.formState.errors.message && touchedFields.contactMessage && (
                                            <p className="mt-1 text-sm text-red-600">{contactForm.formState.errors.message.message}</p>
                                        )}
                                    </div>

                                    <Button
                                        type="submit"
                                        variant="gradient"
                                        size="lg"
                                        fullWidth
                                        disabled={!contactForm.formState.isValid || isLoading}
                                        isLoading={isLoading}
                                    >
                                        {isLoading ? 'Sending...' : 'Send Message'}
                                    </Button>
                                </form>
                            </div>
                        )}

                        {/* Success View */}
                        {currentView === 'success' && (
                            <div className="p-8 text-center">
                                {/* Icon */}
                                <div className="w-16 h-16 bg-bibi-blue-50 rounded-full flex items-center justify-center mx-auto mb-6">
                                    {submitIconType === 'home' && <HomeIcon size="xl" variant="primary" className="text-bibi-blue" />}
                                    {submitIconType === 'star' && <StarIcon size="xl" variant="primary" className="text-bibi-blue" />}
                                    {submitIconType === 'heart' && <HeartIcon size="xl" variant="primary" className="text-bibi-blue" />}
                                    {submitIconType === 'handshake' && <HandshakeIcon size="xl" variant="primary" className="text-bibi-blue" />}
                                    {submitIconType === 'usergroup' && <UserGroupIcon size="xl" variant="primary" className="text-bibi-blue" />}
                                    {submitIconType === 'briefcase' && <BriefcaseIcon size="xl" variant="primary" className="text-bibi-blue" />}
                                    {submitIconType === 'mail' && <MailIcon size="xl" variant="primary" className="text-bibi-blue" />}
                                    {submitIconType === 'check' && <CheckCircleIcon size="xl" variant="primary" className="text-bibi-blue" />}
                                </div>

                                {/* Title */}
                                <h3 className="text-xl font-semibold text-gray-900 mb-6 text-center">
                                    {submitTitle}
                                </h3>

                                {/* Message */}
                                <div className="text-left max-w-lg mx-auto">
                                    <pre className="text-gray-700 leading-relaxed whitespace-pre-wrap font-sans text-sm">
                                        {submitMessage}
                                    </pre>
                                </div>

                                <Button
                                    variant="ghost"
                                    onClick={() => {
                                        setCurrentView('initial');
                                        initialForm.reset();
                                        interestForm.reset();
                                        contactForm.reset();
                                        setZipValidation(null);
                                        setSubmitMessage('');
                                        setSubmitTitle('');
                                        setSubmitIconType('check');
                                        setTouchedFields({}); // Reset touched fields
                                    }}
                                    className="mt-8 text-bibi-blue hover:text-bibi-blue-dark font-medium underline"
                                >
                                    Submit another response
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};
